/**
 * Tracking Period (client mirror of backend/utils/trackingPeriod.js)
 * ---------------------------------------------------------------------------
 * A closing stock count draws a line under a day. Everything up to and
 * including it is history the owner reconciled by hand; the app's tracking
 * starts the NEXT day and runs to today.
 *
 *   counted 2026-08-31  ->  tracking starts 2026-09-01
 *   today   2026-09-10  ->  10 completed days (1st through 10th)
 *
 * Both ends are inclusive — Sep 1 to Sep 10 is ten days, not nine.
 *
 * This exists so the onboarding footer can show the owner what their chosen
 * date means BEFORE they commit it, with no round-trip. The server recomputes
 * the same thing on finalise and is the record of truth; the two must agree,
 * which is why both are the same three lines of arithmetic on ISO strings.
 */

const IST_TIMEZONE = 'Asia/Kolkata';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad2 = (value) => String(value).padStart(2, '0');

/** Today (or any instant) as an IST 'YYYY-MM-DD' string. */
export const getISTDateString = (input = new Date()) => {
  if (typeof input === 'string' && ISO_DATE_RE.test(input)) return input;
  return new Date(input).toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });
};

/** 'YYYY-MM-DD' or null — never a half-parsed date. */
export const toISODate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).trim().slice(0, 10);
  return ISO_DATE_RE.test(text) ? text : null;
};

const isoToUTCms = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

/** Move an ISO date by whole days. Calendar-safe across months and years. */
export const shiftISODate = (iso, days) => {
  const shifted = new Date(isoToUTCms(iso) + days * MS_PER_DAY);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
};

/** Whole days from `fromISO` to `toISO` (negative when `toISO` is earlier). */
export const diffISODays = (fromISO, toISO) =>
  Math.round((isoToUTCms(toISO) - isoToUTCms(fromISO)) / MS_PER_DAY);

/**
 * The tracking period implied by a closing stock date.
 * Shape matches GET /api/onboarding/tracking-period exactly.
 */
export const computeTrackingPeriod = (closingStockDate, today = getISTDateString()) => {
  const todayISO = toISODate(today) || getISTDateString();
  const closingISO = toISODate(closingStockDate);

  if (!closingISO) {
    return {
      closingStockDate: null,
      trackingStartDate: null,
      today: todayISO,
      completedDays: 0,
      isTracking: false,
    };
  }

  const trackingStartDate = shiftISODate(closingISO, 1);
  // A count filed for today has produced no tracked day yet, hence the floor.
  const completedDays = Math.max(0, diffISODays(trackingStartDate, todayISO) + 1);

  return {
    closingStockDate: closingISO,
    trackingStartDate,
    today: todayISO,
    completedDays,
    isTracking: completedDays > 0,
  };
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * '01 Sep 2026'.
 *
 * Spelled out from the ISO parts rather than through toLocaleDateString: the
 * en-GB short month is 'Sept' on newer ICU builds and 'Sep' on older ones, and
 * this date is one the owner cross-checks against their own paperwork — it
 * should not read differently depending on which browser opened the page.
 */
export const formatTrackingDate = (iso) => {
  const date = toISODate(iso);
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${day} ${MONTH_ABBR[Number(month) - 1]} ${year}`;
};

/**
 * The one sentence the owner reads: where tracking starts and how much of it
 * is already behind them. Returns '' when there is nothing to say.
 */
export const describeTrackingPeriod = (period) => {
  if (!period || !period.trackingStartDate) return '';
  const start = formatTrackingDate(period.trackingStartDate);
  if (!period.completedDays) {
    return `Tracking starts ${start}`;
  }
  const days = period.completedDays;
  return `Tracking from ${start} · ${days} day${days === 1 ? '' : 's'} to today`;
};
